/**
 * Routing Attempt Builder
 * 
 * Builds a routing attempt record from a RoutingResult.
 * This is the bridge between the orchestrator and the routing attempts database.
 */

import type { RoutingResult, IngestionSource } from '../routingOrchestrator';
import type { 
  RoutingAttemptSource, 
  RoutingAttemptResult,
  AttemptScoring,
  GuardrailsApplied,
  DevOverridesContext,
  DevOverrideAttribution,
  RetryContext,
} from './routingAttemptTypes';
import type { CreateRoutingAttemptInput } from '@/hooks/useRoutingAttempts';

// --- Source Mapping ---

function mapIngestionSourceToAttemptSource(source: IngestionSource): RoutingAttemptSource {
  switch (source) {
    case 'simulation':
      return 'SIMULATION';
    case 'retry':
      return 'RETRY';
    case 'real':
    default:
      return 'LIVE';
  }
}

// --- Outcome Mapping ---

function mapOutcomeToResult(outcome: RoutingResult['outcome']): RoutingAttemptResult {
  switch (outcome) {
    case 'ROUTED':
      return 'ROUTED';
    case 'UNSOLD':
      return 'UNSOLD';
    case 'RETRY_QUEUED':
      return 'RETRY_QUEUED';
    case 'STOCKED':
      return 'STOCKED';
    case 'VALIDATION_FAILED':
      return 'VALIDATION_FAILED';
  }
}

// --- ID Generator ---

let attemptCounter = 0;

function generateAttemptId(): string {
  attemptCounter++;
  return `attempt_${Date.now()}_${attemptCounter}`;
}

// --- Builder ---

export interface BuildRoutingAttemptOptions {
  source: IngestionSource;
  attemptNumber: number;
  routingEventId?: string;
  simulationRunId?: string;
  devOverridesApplied?: boolean;
  eligibilitySource?: 'REAL' | 'DEV_INJECTED';
  devInjectedAgentIds?: string[];
  /** Per-agent attribution of which checks were bypassed */
  agentAttributions?: Record<string, DevOverrideAttribution>;
  /** Retry-specific context (for retry attempts) */
  retryContext?: {
    previousAttemptNumber: number;
    previousStatus: string;
    retryReason?: string;
  };
}

export function buildRoutingAttempt(
  result: RoutingResult,
  options: BuildRoutingAttemptOptions
): CreateRoutingAttemptInput {
  const { 
    source, 
    attemptNumber, 
    routingEventId, 
    simulationRunId, 
    devOverridesApplied = false,
    eligibilitySource,
    devInjectedAgentIds,
    agentAttributions,
    retryContext: inputRetryContext,
  } = options;
  const { lead, observability, deliveryImpact, outcome } = result;
  
  // Build scoring data
  const scoring: AttemptScoring = {
    priorityScore: observability.priorityScore,
    bidScore: observability.allocationBreakdown?.bidScore,
    qualityScore: observability.allocationBreakdown?.qualityScore,
    finalScore: observability.allocationBreakdown?.finalScore,
  };
  
  // Build guardrails applied
  const guardrailSummary = observability.guardrailSummary;
  const guardrails: GuardrailsApplied = {
    pacingCap: guardrailSummary?.pacingCap !== undefined && guardrailSummary.pacingCap < 1.0,
    poolBoost: guardrailSummary?.poolBoost !== undefined && guardrailSummary.poolBoost > 0,
    retryPriority: guardrailSummary?.retryMultiplier !== undefined && guardrailSummary.retryMultiplier > 1.0,
  };
  
  // Build DEV overrides context for full attribution
  const devOverridesContext: DevOverridesContext | undefined = devOverridesApplied
    ? {
        applied: true,
        eligibilitySource: eligibilitySource,
        injectedAgentIds: devInjectedAgentIds,
        agentAttributions: agentAttributions,
      }
    : undefined;
  
  // Build retry context for retry attempts
  // CRITICAL: Retries always re-evaluate eligibility and DEV overrides from scratch
  const isRetryAttempt = source === 'retry';
  const retryContext: RetryContext | undefined = isRetryAttempt
    ? {
        isRetry: true,
        previousAttemptNumber: inputRetryContext?.previousAttemptNumber ?? (attemptNumber - 1),
        previousStatus: inputRetryContext?.previousStatus ?? result.previousStatus,
        devOverridesReEvaluated: true, // Always true after the fix
        eligibilityComputedFresh: true, // Always true after the fix
        retryReason: inputRetryContext?.retryReason ?? 'retry_worker',
      }
    : undefined;
  
  return {
    id: generateAttemptId(),
    leadId: lead.id,
    attemptNumber, // Use the pre-calculated attempt number
    source: mapIngestionSourceToAttemptSource(source),
    result: mapOutcomeToResult(outcome),
    
    // Agent assignment
    agentId: deliveryImpact.agentId,
    agentName: deliveryImpact.agentName,
    chargedAmount: deliveryImpact.chargedAmount,
    
    // Scoring
    scoring,
    
    // Eligibility
    eligibleAgentsCount: observability.eligibleCount,
    ineligibleAgentsCount: observability.ineligibleCount,
    totalAgentsEvaluated: observability.totalAgentsEvaluated,
    
    // Guardrails
    guardrailsApplied: observability.guardrailsApplied,
    guardrails,
    
    // DEV overrides
    devOverridesApplied,
    devOverridesContext,
    
    // Retry context
    retryContext,
    
    // Rejection info
    rejectionCode: observability.rejectionCode,
    rejectionReason: observability.rejectionReason,
    
    // Linkage
    routingEventId,
    simulationRunId,
    
    // Timing
    startedAt: observability.startedAt,
    completedAt: observability.completedAt,
    durationMs: observability.durationMs,
  };
}
